<template>
  <b-card title="Configuration Mode">
    <b-overlay
      :show="loading"
      blur="10px"
      opacity="0.30"
      rounded="sm"
      variant="light"
    >
      <b-form
        @submit.prevent
        @submit="saveRiderParcelSettings"
      >
        <b-row>
          <b-col cols="12">
            <b-card class="mb-0 mt-0">
              <b-form-radio-group
                v-model="form.rider_parcel_assignment.is_default"
                :options="radioOptions"
                class="demo-inline-spacing mb-3"
                value-field="value"
                text-field="text"
                disabled-field="disabled"
              />

              <!-- Default Mode: show daily parcel limit + unlimited -->
              <div v-if="form.rider_parcel_assignment.is_default">
                <b-form-group
                  label="Daily Parcel Limit"
                >
                  <b-row>
                    <b-col cols="6">
                      <b-form-input
                        id="daily_parcel_limit"
                        v-model="form.rider_parcel_assignment.daily_parcel_limit"
                        type="number"
                        :disabled="form.rider_parcel_assignment.unlimited"
                        placeholder="100"
                        autocomplete="off"
                      />
                    </b-col>
                    <b-col
                      cols="6"
                      class="d-flex align-items-center"
                    >
                      <b-form-checkbox
                        v-model="form.rider_parcel_assignment.unlimited"
                      >
                        Unlimited
                      </b-form-checkbox>
                    </b-col>
                  </b-row>
                </b-form-group>
                <div>
                  This limit applies to all riders unless overridden at warehouse or rider level.
                </div>
              </div>

              <!-- Custom Mode: show level configuration options -->
              <div v-else>
                <b-form-checkbox
                  v-model="form.rider_parcel_assignment.warehouse_level_enabled"
                  class="mb-1"
                >
                  Warehouse Level Configuration
                </b-form-checkbox>
                <b-form-checkbox
                  v-model="form.rider_parcel_assignment.rider_level_enabled"
                >
                  Rider Level Configuration
                </b-form-checkbox>
                <div class="mt-2">
                  Enable configuration at specific organizational levels. Rider-level limits override warehouse limits, which override global defaults.
                </div>
              </div>
            </b-card>
          </b-col>
        </b-row>

        <!-- submit -->
        <b-row>
          <b-col class="d-flex justify-content-end">
            <b-button
              v-can="'edit-rider-parcel-limit-assignment'"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              type="submit"
              class="mt-1 custom-button-color"
            >
              Submit
            </b-button>
          </b-col>
        </b-row>
      </b-form>
    </b-overlay>
  </b-card>
</template>

<script>
import {
  BRow, BCol, BForm, BFormGroup, BButton, BCard, BOverlay, BFormRadioGroup, BFormInput, BFormCheckbox,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import SuccessMessage from '@core/mixins/SuccessMessage'
import ErrorMessage from '@core/mixins/ErrorMessage'
import { RepositoryFactory } from '@/repository/RepositoryFactory'

const RiderRepository = RepositoryFactory.get('rider')

export default {
  components: {
    BRow,
    BCol,
    BForm,
    BFormGroup,
    BButton,
    BCard,
    BOverlay,
    BFormRadioGroup,
    BFormInput,
    BFormCheckbox,
  },
  directives: {
    Ripple,
  },
  mixins: [SuccessMessage, ErrorMessage],
  data() {
    return {
      loading: false,
      form: {
        rider_parcel_assignment: {
          is_default: true,
          daily_parcel_limit: null,
          unlimited: false,
          warehouse_level_enabled: false,
          rider_level_enabled: false,
        },
      },
      radioOptions: [
        { text: 'Default Mode', value: true },
        { text: 'Custom Mode', value: false },
      ],
      roleoptions: [],
      error: '',
      activeTab: 0,
    }
  },
  created() {
    this.fetchRiderParcelSettings()
  },

  methods: {
    // GET /admin/settings/rider-parcel-limit
    async fetchRiderParcelSettings() {
      this.loading = true
      try {
        const res = await RiderRepository.getriderParcelLimitSettings()
        // Adjust according to your actual API shape
        const payload = res.data?.data || res.data || {}
        const value = payload.value || payload

        this.form.rider_parcel_assignment.is_default = !!value.is_default
        this.form.rider_parcel_assignment.daily_parcel_limit = value.default_limit
        this.form.rider_parcel_assignment.unlimited = !!value.default_is_unlimited
        this.form.rider_parcel_assignment.warehouse_level_enabled = !!value.enable_branch_limit
        this.form.rider_parcel_assignment.rider_level_enabled = !!value.enable_rider_limit
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.loading = false
      }
    },

    // PUT /admin/settings/rider-parcel-limit
    async saveRiderParcelSettings() {
      this.loading = true
      try {
        // compute default_limit
        let defaultLimit = null
        if (
          !this.form.rider_parcel_assignment.unlimited
          && this.form.rider_parcel_assignment.daily_parcel_limit !== null
          && this.form.rider_parcel_assignment.daily_parcel_limit !== ''
        ) {
          defaultLimit = parseInt(this.form.rider_parcel_assignment.daily_parcel_limit, 10)
        }

        const payload = {
          is_default: !!this.form.rider_parcel_assignment.is_default,
          default_limit: defaultLimit,
          default_is_unlimited: !!this.form.rider_parcel_assignment.unlimited,
          enable_branch_limit: !!this.form.rider_parcel_assignment.warehouse_level_enabled,
          enable_rider_limit: !!this.form.rider_parcel_assignment.rider_level_enabled,
        }

        const res = await RiderRepository.updateRiderParcelLimitSettings(payload)

        if (res.status === 200) {
          this.showSuccessMessage('Rider assignment settings updated successfully')
        } else {
          this.showErrorMessage('Cannot update rider assignment settings')
        }
      } catch (e) {
        this.convertAndNotifyError(e)
      } finally {
        this.loading = false
      }
    },
  },
}
</script>
<style lang="scss" scoped>
@import '@core/scss/vue/required.scss';
.custom-button-color {
  background-color: #4186f5 !important;
  border-color: #4186f5 !important;
}
</style>
